import React, { FC, Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  CollectionPaymentStatus,
  CollectionProgressItem,
  CollectionStatus,
  Money as MoneyModel,
  TrackRecordProject,
} from 'api-client';
import {
  Container,
  CustomLink,
  DashboardHeader,
  Heading,
  Money,
  PrimaryHeading,
  Section,
  Skeleton,
} from 'components';
import { FilterBar } from 'components/common/filters';
import { HeadingType } from 'components/common/typography/headings/Heading/Heading';
import {
  formatCollectionDate,
  getCollectionStatusLabel,
  getPaymentStatusLabel,
} from 'components/pages/detailed-project/ProjectCollection';
import { ProjectSearchField } from 'components/pages/projects/filter/controls';
import { createProjectProvider } from 'di';
import { useErrorCallback } from 'hooks';
import { cn, declension, RouteDictionary } from 'utils';

import styles from './TrackRecord.module.scss';
import {
  formatDays,
  formatMillions,
  formatShare,
  getValidDays,
  matchesQuery,
  nextSort,
  parseSort,
  parseStatus,
  projectTitle,
  Row,
  Sort,
  SortKey,
  sortRows,
  StatusFilter,
  statusParam,
  summarize,
  Summary,
  toRow,
} from './trackRecordData';

const PAGE_TITLE = 'Трек рекорд';

const FILTERS: ReadonlyArray<{ value: StatusFilter; label: string }> = [
  { value: 'all', label: 'Все статусы' },
  { value: CollectionStatus.OPEN, label: 'В работе' },
  { value: CollectionStatus.CLOSED, label: 'Завершено' },
];

/** Сортировка для телефона, где нет заголовков колонок. */
const SORT_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: '', label: 'По номеру проекта' },
  { value: 'target:desc', label: 'Целевая сумма: больше' },
  { value: 'target:asc', label: 'Целевая сумма: меньше' },
  { value: 'paid:desc', label: 'Поступило: больше' },
  { value: 'paid:asc', label: 'Поступило: меньше' },
  { value: 'achievement:desc', label: 'Достижение: выше' },
  { value: 'achievement:asc', label: 'Достижение: ниже' },
  { value: 'finish:desc', label: 'Плановая дата: позже' },
  { value: 'finish:asc', label: 'Плановая дата: раньше' },
  { value: 'days:desc', label: 'Время взыскания: дольше' },
  { value: 'days:asc', label: 'Время взыскания: быстрее' },
];

const Dash = () => <span className={styles.dash}>—</span>;

const MoneyCell: FC<{ money?: MoneyModel; compact?: boolean }> = ({ money, compact }) =>
  money ? <Money money={money} compact={compact} /> : <Dash />;

/** Полная сумма в таблице; на телефоне, в трети ширины, — сокращённая ("24,2 млн"). */
const FigureCell: FC<{ money?: MoneyModel }> = ({ money }) =>
  money ? (
    <>
      <span className={styles.moneyFull}>
        <Money money={money} />
      </span>
      <span className={styles.moneyShort}>
        <Money money={money} compact />
      </span>
    </>
  ) : (
    <Dash />
  );

const NOTHING_PLANNED = 'Нет запланированных платежей';

/** Остаток; у проекта в работе с нулевым остатком — пояснение, почему он ещё не завершён. */
const RemainingCell: FC<{ row: Row; compact?: boolean }> = ({ row, compact }) => (
  <>
    <MoneyCell money={row.plannedSum} compact={compact} />
    {row.nothingPlanned && <span className={styles.cellNote}>{NOTHING_PLANNED}</span>}
  </>
);

const Status: FC<{ status?: CollectionStatus }> = ({ status }) => (
  <span className={styles.status}>
    <span
      className={cn(
        styles.statusDot,
        status === CollectionStatus.OPEN && styles.statusDotOpen,
        status === CollectionStatus.CLOSED && styles.statusDotClosed
      )}
      aria-hidden="true"
    />
    {getCollectionStatusLabel(status)}
  </span>
);

const Fact: FC<{ label: string; className?: string; children: React.ReactNode }> = ({
  label,
  className,
  children,
}) => (
  <div className={cn(styles.fact, className)}>
    <dt>{label}</dt>
    <dd>{children}</dd>
  </div>
);

const DaysValue: FC<{ days?: number }> = ({ days }) =>
  days === undefined ? <Dash /> : <>{formatDays(days)}</>;

/**
 * Раскрытая строка: только то, чего нет в самой строке, — категория, даты и график
 * платежей. Суммы и статус уже в строке; повторяются они лишь для проекта из нескольких
 * строк реестра, где строка таблицы показывает их сумму. Остаток и плановая дата здесь
 * видны только на планшете, где для них нет колонок.
 */
const CollectionDetails: FC<{ row: Row; item: CollectionProgressItem; multiple: boolean }> = ({
  row,
  item,
  multiple,
}) => (
  <section className={styles.detailsItem} aria-label={`Взыскание ${item.sequence}`}>
    {multiple && <h3 className={styles.detailsHeading}>Взыскание {item.sequence}</h3>}
    <div className={styles.detailsGrid}>
      <dl className={styles.facts}>
        {multiple && (
          <>
            <Fact label="Статус">
              <Status status={item.status} />
            </Fact>
            <Fact label="Целевая сумма">
              <MoneyCell money={item.targetSum} />
            </Fact>
            <Fact label="Поступило">
              <MoneyCell money={item.paidSum} />
            </Fact>
            <Fact label="Остаток">
              <MoneyCell money={item.plannedSum} />
            </Fact>
            <Fact label="Достижение">
              {item.targetAchievementPercent == null ? (
                <Dash />
              ) : (
                formatShare(item.targetAchievementPercent)
              )}
            </Fact>
          </>
        )}
        {!multiple && (
          <Fact label="Остаток" className={styles.tabletOnly}>
            <RemainingCell row={row} />
          </Fact>
        )}
        <Fact label="Плановая дата" className={multiple ? undefined : styles.tabletOnly}>
          {item.finishPlannedAt ? formatCollectionDate(item.finishPlannedAt) : <Dash />}
        </Fact>
        <Fact label="Категория урегулирования">{item.strategyCategory || <Dash />}</Fact>
        <Fact label="Начало работы">
          {item.addedAt ? formatCollectionDate(item.addedAt) : <Dash />}
        </Fact>
        <Fact label="Закрыт для инвестора">
          {item.publicClosedAt ? formatCollectionDate(item.publicClosedAt) : <Dash />}
        </Fact>
        <Fact label="Время взыскания">
          <DaysValue days={getValidDays(item)} />
        </Fact>
      </dl>

      <div className={styles.schedule}>
        <h4 className={styles.scheduleHeading}>График платежей</h4>
        {item.payments.length === 0 ? (
          <p className={styles.scheduleEmpty}>Платежей по этому взысканию пока нет.</p>
        ) : (
          <table className={styles.scheduleTable}>
            <thead>
              <tr>
                <th scope="col">Дата</th>
                <th scope="col">Статус</th>
                <th scope="col" className={styles.num}>
                  Сумма
                </th>
              </tr>
            </thead>
            <tbody>
              {item.payments.map((payment) => (
                <tr key={payment.uuid}>
                  <td className={styles.date}>
                    {payment.paymentDate ? formatCollectionDate(payment.paymentDate) : <Dash />}
                  </td>
                  <td>
                    <span className={styles.status}>
                      <span
                        className={cn(
                          styles.statusDot,
                          payment.status === CollectionPaymentStatus.PAID && styles.statusDotClosed,
                          payment.status === CollectionPaymentStatus.PLANNED &&
                            styles.statusDotPlanned
                        )}
                        aria-hidden="true"
                      />
                      {getPaymentStatusLabel(payment.status)}
                    </span>
                  </td>
                  <td className={styles.num}>
                    <FigureCell money={payment.totalSum} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  </section>
);

const Details: FC<{ row: Row }> = ({ row }) => (
  <>
    {row.project.items.map((item) => (
      <CollectionDetails
        key={item.uuid}
        row={row}
        item={item}
        multiple={row.project.items.length > 1}
      />
    ))}
  </>
);

const ToggleButton: FC<{
  expanded: boolean;
  controls: string;
  name: string;
  onClick: () => void;
}> = ({ expanded, controls, name, onClick }) => (
  <button
    type="button"
    className={cn(styles.toggle, expanded && styles.toggleExpanded)}
    aria-expanded={expanded}
    aria-controls={controls}
    aria-label={`${expanded ? 'Скрыть' : 'Показать'} подробности и график платежей: ${name}`}
    onClick={onClick}
  >
    <svg className={styles.chevron} width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
      <path d="M4 6l4 4 4-4" fill="none" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  </button>
);

const SortIcon: FC<{ direction?: 'asc' | 'desc' }> = ({ direction }) => (
  <svg
    className={cn(styles.sortIcon, direction && styles.sortIconActive)}
    width="12"
    height="12"
    viewBox="0 0 12 12"
    aria-hidden="true"
  >
    {direction !== 'desc' && (
      <path d="M3 5l3-3 3 3" fill="none" stroke="currentColor" strokeWidth="1.25" />
    )}
    {direction !== 'asc' && (
      <path d="M3 7l3 3 3-3" fill="none" stroke="currentColor" strokeWidth="1.25" />
    )}
  </svg>
);

/** Заголовок колонки с сортировкой: кнопка внутри th, состояние — в aria-sort на th. */
const SortableHead: FC<{
  label: string;
  sortKey: SortKey;
  sort?: Sort;
  onSort: (key: SortKey) => void;
  className?: string;
}> = ({ label, sortKey, sort, onSort, className }) => {
  const direction = sort?.key === sortKey ? sort.direction : undefined;

  return (
    <th
      scope="col"
      className={cn(styles.num, className)}
      aria-sort={direction ? (direction === 'asc' ? 'ascending' : 'descending') : undefined}
    >
      <button type="button" className={styles.sortButton} onClick={() => onSort(sortKey)}>
        {label}
        <SortIcon direction={direction} />
      </button>
    </th>
  );
};

type RowProps = {
  row: Row;
  expanded: boolean;
  onToggle: (uuid: string) => void;
};

/** Колонок в таблице — для colSpan раскрытой строки и пустого состояния. */
const COLUMN_COUNT = 9;

const TrackRecordTableRow: FC<RowProps> = ({ row, expanded, onToggle }) => {
  const { project } = row;
  const detailsId = `track-record-${project.projectUuid}`;
  const name = projectTitle(project);

  return (
    <Fragment>
      <tr className={cn(styles.row, expanded && styles.rowExpanded)}>
        <td className={styles.projectCell}>
          <CustomLink
            className={styles.projectName}
            to={RouteDictionary.PROJECT_COLLECTION}
            params={{ uuid: project.projectUuid }}
            title={name}
          >
            {name}
          </CustomLink>
        </td>
        <td>
          <Status status={row.status} />
        </td>
        <td className={styles.num}>
          <MoneyCell money={row.targetSum} />
        </td>
        <td className={styles.num}>
          <MoneyCell money={row.paidSum} />
        </td>
        <td className={cn(styles.num, styles.wideOnly)}>
          <RemainingCell row={row} />
        </td>
        <td className={styles.num}>
          {row.achievement === undefined ? <Dash /> : formatShare(row.achievement)}
        </td>
        <td className={cn(styles.num, styles.wideOnly)}>
          {row.finishPlannedAt ? formatCollectionDate(row.finishPlannedAt) : <Dash />}
        </td>
        <td className={styles.num}>
          <DaysValue days={row.collectionDays} />
        </td>
        <td className={styles.toggleCell}>
          <ToggleButton
            expanded={expanded}
            controls={detailsId}
            name={name}
            onClick={() => onToggle(project.projectUuid)}
          />
        </td>
      </tr>
      {/* Строка есть всегда, чтобы aria-controls указывал на существующий элемент. */}
      <tr id={detailsId} className={styles.detailsRow} hidden={!expanded}>
        <td colSpan={COLUMN_COUNT}>{expanded && <Details row={row} />}</td>
      </tr>
    </Fragment>
  );
};

const Metric: FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className={styles.metric}>
    <span className={styles.metricLabel}>{label}</span>
    <span className={styles.metricValue}>{children}</span>
  </div>
);

const TrackRecordMobileCard: FC<RowProps> = ({ row, expanded, onToggle }) => {
  const { project } = row;
  const detailsId = `track-record-mobile-${project.projectUuid}`;
  const name = projectTitle(project);

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <div className={styles.cardTitle}>
          <CustomLink
            className={styles.projectName}
            to={RouteDictionary.PROJECT_COLLECTION}
            params={{ uuid: project.projectUuid }}
          >
            {name}
          </CustomLink>
          <Status status={row.status} />
        </div>
        <ToggleButton
          expanded={expanded}
          controls={detailsId}
          name={name}
          onClick={() => onToggle(project.projectUuid)}
        />
      </div>
      <div className={styles.metrics}>
        <Metric label="Цель">
          <MoneyCell money={row.targetSum} compact />
        </Metric>
        <Metric label="Поступило">
          <MoneyCell money={row.paidSum} compact />
        </Metric>
        <Metric label="Остаток">
          <RemainingCell row={row} compact />
        </Metric>
        <Metric label="Достижение">
          {row.achievement === undefined ? <Dash /> : formatShare(row.achievement)}
        </Metric>
        <Metric label="Плановая дата">
          {row.finishPlannedAt ? formatCollectionDate(row.finishPlannedAt) : <Dash />}
        </Metric>
        <Metric label="Время взыскания">
          <DaysValue days={row.collectionDays} />
        </Metric>
      </div>
      <div id={detailsId} className={styles.details} hidden={!expanded}>
        {expanded && <Details row={row} />}
      </div>
    </div>
  );
};

const SummaryFigure: FC<{ label: string; value: React.ReactNode; note?: React.ReactNode }> = ({
  label,
  value,
  note,
}) => (
  <div className={styles.figure}>
    <dt className={styles.figureLabel}>{label}</dt>
    <dd className={styles.figureValue}>{value}</dd>
    <dd className={styles.figureNote}>{note}</dd>
  </div>
);

const TrackRecordSummary: FC<{ summary: Summary }> = ({ summary }) => {
  const { total, closed, paidShare, reachedTarget, medianDays } = summary;

  return (
    <dl className={styles.summary} aria-label="Итоги взыскания по всем проектам">
      <SummaryFigure
        label="Завершено"
        value={`${closed} из ${total}`}
        note={declension(total, ['проекта', 'проектов', 'проектов'])}
      />
      <SummaryFigure
        label="Поступило от целевой суммы"
        value={paidShare ? formatShare(paidShare.paid / paidShare.target) : <Dash />}
        note={
          paidShare && `${formatMillions(paidShare.paid)} из ${formatMillions(paidShare.target)}`
        }
      />
      <SummaryFigure
        label="Достигли целевой суммы"
        value={
          reachedTarget ? formatShare(reachedTarget.reached / reachedTarget.measured) : <Dash />
        }
        note={
          reachedTarget &&
          `${reachedTarget.reached} из ${reachedTarget.measured} ${declension(
            reachedTarget.measured,
            ['завершённого взыскания', 'завершённых взысканий', 'завершённых взысканий']
          )}`
        }
      />
      <SummaryFigure
        label="Медиана времени взыскания"
        value={medianDays ? formatDays(medianDays.days) : <Dash />}
        note={
          medianDays &&
          `по ${medianDays.measured} ${declension(medianDays.measured, [
            'завершённому взысканию',
            'завершённым взысканиям',
            'завершённым взысканиям',
          ])}`
        }
      />
    </dl>
  );
};

const SKELETON_ROWS = 8;

const TableSkeleton = () => (
  <>
    {Array.from({ length: SKELETON_ROWS }, (_, index) => (
      // По ячейке на колонку, с шириной как у данных, — чтобы колонки не прыгали после загрузки.
      <tr key={index}>
        <td className={styles.projectCell}>
          <Skeleton height={20} width="70%" />
        </td>
        <td>
          <Skeleton height={20} width={88} />
        </td>
        <td className={styles.num}>
          <Skeleton height={20} width={116} />
        </td>
        <td className={styles.num}>
          <Skeleton height={20} width={116} />
        </td>
        <td className={cn(styles.num, styles.wideOnly)}>
          <Skeleton height={20} width={116} />
        </td>
        <td className={styles.num}>
          <Skeleton height={20} width={52} />
        </td>
        <td className={cn(styles.num, styles.wideOnly)}>
          <Skeleton height={20} width={80} />
        </td>
        <td className={styles.num}>
          <Skeleton height={20} width={64} />
        </td>
        <td className={styles.toggleCell}>
          <Skeleton height={20} width={32} />
        </td>
      </tr>
    ))}
  </>
);

const CardsSkeleton = () => (
  <>
    {Array.from({ length: 4 }, (_, index) => (
      <div key={index} className={styles.card}>
        <Skeleton height={20} width="70%" />
        <Skeleton height={56} />
      </div>
    ))}
  </>
);

type LoadState = 'loading' | 'error' | 'ready';

/**
 * "Трек рекорд" — ход взыскания по всем проектам платформы, у которых он есть.
 *
 * Одна строка таблицы на проект; порядок по умолчанию — по номеру проекта по убыванию —
 * задаёт crowdfunding. Фильтр, поиск и сортировка живут в строке запроса, итоги над
 * таблицей считаются по всем проектам. Строка раскрывается в категорию, даты и график платежей.
 */
const TrackRecord = () => {
  const handleError = useErrorCallback();
  const [state, setState] = useState<LoadState>('loading');
  const [projects, setProjects] = useState<ReadonlyArray<TrackRecordProject>>([]);
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const [params, setParams] = useSearchParams();

  const filter = parseStatus(params.get('status'));
  const sort = parseSort(params.get('sort'), params.get('dir'));
  const query = params.get('q') ?? '';

  const updateParams = useCallback(
    (patch: Record<string, string | undefined>) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current);
          Object.entries(patch).forEach(([key, value]) =>
            value ? next.set(key, value) : next.delete(key)
          );

          return next;
        },
        { replace: true }
      );
    },
    [setParams]
  );

  const load = useCallback(async () => {
    setState('loading');
    try {
      const provider = await createProjectProvider();
      const trackRecord = await provider.getCollectionTrackRecord();
      setProjects(trackRecord.projects);
      setState('ready');
    } catch (error: any) {
      setState('error');
      await handleError(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = `${PAGE_TITLE} · Conomica`;

    return () => {
      document.title = previousTitle;
    };
  }, []);

  const rows = useMemo(() => projects.map(toRow), [projects]);
  const summary = useMemo(() => summarize(rows), [rows]);
  const matchedRows = useMemo(() => rows.filter((row) => matchesQuery(row, query)), [rows, query]);

  const counts = useMemo(
    () => ({
      all: matchedRows.length,
      [CollectionStatus.OPEN]: matchedRows.filter((row) => row.status === CollectionStatus.OPEN)
        .length,
      [CollectionStatus.CLOSED]: matchedRows.filter((row) => row.status === CollectionStatus.CLOSED)
        .length,
    }),
    [matchedRows]
  );

  const visibleRows = useMemo(
    () =>
      sortRows(
        filter === 'all' ? matchedRows : matchedRows.filter((row) => row.status === filter),
        sort
      ),
    [matchedRows, filter, sort]
  );

  const toggle = useCallback((uuid: string) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(uuid)) {
        next.delete(uuid);
      } else {
        next.add(uuid);
      }

      return next;
    });
  }, []);

  const setFilter = (value: StatusFilter) => updateParams({ status: statusParam(value) });
  const setSort = (value?: Sort) => updateParams({ sort: value?.key, dir: value?.direction });
  const sortBy = (key: SortKey) => setSort(nextSort(sort, key));
  const resetFilters = () => updateParams({ status: undefined, q: undefined });

  const loading = state === 'loading';
  const ready = state === 'ready';
  const narrowed = filter !== 'all' || query.trim() !== '';

  const emptyState =
    rows.length === 0 ? (
      'Данных по взысканию пока нет.'
    ) : (
      <>
        Ничего не найдено.{' '}
        <button type="button" className={styles.linkButton} onClick={resetFilters}>
          Сбросить поиск и статус
        </button>
      </>
    );

  return (
    <Container>
      <Section>
        <DashboardHeader>
          <DashboardHeader.Main>
            <PrimaryHeading>
              <Heading headingType={HeadingType.H1}>{PAGE_TITLE}</Heading>
            </PrimaryHeading>
          </DashboardHeader.Main>
        </DashboardHeader>
        <p className={styles.intro}>
          Ход взыскания по проектам платформы: целевые суммы, поступления и график платежей по
          данным департамента взыскания.
        </p>

        {loading && <Skeleton className={styles.summarySkeleton} height={108} />}
        {ready && rows.length > 0 && <TrackRecordSummary summary={summary} />}

        {state === 'error' ? (
          <div className={styles.error} role="alert">
            <p className={styles.errorText}>Не удалось загрузить данные по взысканию.</p>
            <button type="button" className={styles.retryButton} onClick={load}>
              Повторить
            </button>
          </div>
        ) : (
          <>
            <FilterBar className={styles.filterBar}>
              <div className={styles.pills} role="group" aria-label="Статус взыскания">
                <FilterBar.Pills>
                  {FILTERS.map((option) => {
                    const active = filter === option.value;

                    return (
                      <FilterBar.Pill
                        key={option.value}
                        active={active}
                        showClear={active && option.value !== 'all'}
                        aria-pressed={active}
                        disabled={!ready || rows.length === 0}
                        // Как на странице проектов: повторный клик по статусу возвращает "Все статусы".
                        onClick={() =>
                          setFilter(active && option.value !== 'all' ? 'all' : option.value)
                        }
                        onClear={() => setFilter('all')}
                      >
                        {option.label}
                        {ready && option.value !== 'all' && ` (${counts[option.value]})`}
                      </FilterBar.Pill>
                    );
                  })}
                </FilterBar.Pills>
              </div>
              <FilterBar.Actions>
                <div className={styles.tools}>
                  <label className={styles.search}>
                    <span className="visually-hidden">Поиск по названию или номеру проекта</span>
                    <ProjectSearchField
                      value={query}
                      onChange={(value) => updateParams({ q: value })}
                    />
                  </label>
                  <label className={styles.sortSelect}>
                    <span className="visually-hidden">Сортировка</span>
                    <select
                      value={sort ? `${sort.key}:${sort.direction}` : ''}
                      onChange={(event) => setSort(parseSort(...splitSort(event.target.value)))}
                    >
                      {SORT_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </FilterBar.Actions>
            </FilterBar>

            <p
              className={cn(styles.resultCount, narrowed ? undefined : 'visually-hidden')}
              role="status"
            >
              {ready &&
                `Показано ${visibleRows.length} из ${rows.length} ${declension(rows.length, [
                  'проекта',
                  'проектов',
                  'проектов',
                ])}`}
            </p>

            <div className={styles.desktop} aria-busy={loading}>
              <table className={styles.table}>
                <caption className="visually-hidden">Ход взыскания по проектам</caption>
                <thead>
                  <tr>
                    <th scope="col" className={styles.projectCell}>
                      Проект
                    </th>
                    <th scope="col">Статус</th>
                    <SortableHead
                      label="Целевая сумма"
                      sortKey="target"
                      sort={sort}
                      onSort={sortBy}
                    />
                    <SortableHead label="Поступило" sortKey="paid" sort={sort} onSort={sortBy} />
                    <th scope="col" className={cn(styles.num, styles.wideOnly)}>
                      Остаток
                    </th>
                    <SortableHead
                      label="Достижение"
                      sortKey="achievement"
                      sort={sort}
                      onSort={sortBy}
                    />
                    <SortableHead
                      label="Плановая дата"
                      sortKey="finish"
                      sort={sort}
                      onSort={sortBy}
                      className={styles.wideOnly}
                    />
                    <SortableHead
                      label="Время взыскания"
                      sortKey="days"
                      sort={sort}
                      onSort={sortBy}
                    />
                    <th scope="col">
                      <span className="visually-hidden">Подробности</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <TableSkeleton />
                  ) : visibleRows.length === 0 ? (
                    <tr>
                      <td colSpan={COLUMN_COUNT} className={styles.empty}>
                        {emptyState}
                      </td>
                    </tr>
                  ) : (
                    visibleRows.map((row) => (
                      <TrackRecordTableRow
                        key={row.project.projectUuid}
                        row={row}
                        expanded={expanded.has(row.project.projectUuid)}
                        onToggle={toggle}
                      />
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className={styles.mobile} aria-busy={loading}>
              {loading ? (
                <CardsSkeleton />
              ) : visibleRows.length === 0 ? (
                <div className={styles.mobileEmpty}>{emptyState}</div>
              ) : (
                visibleRows.map((row) => (
                  <TrackRecordMobileCard
                    key={row.project.projectUuid}
                    row={row}
                    expanded={expanded.has(row.project.projectUuid)}
                    onToggle={toggle}
                  />
                ))
              )}
            </div>
          </>
        )}
      </Section>
    </Container>
  );
};

const splitSort = (value: string): [string | null, string | null] => {
  const [key, direction] = value.split(':');

  return [key || null, direction || null];
};

export default TrackRecord;
